// CHUNK 18: My synastries dashboard at /minhas-sinastrias
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface SynRow {
  id:                  string;
  partner_name:        string;
  star_score:          number | null;
  compatibility_score: number | null;
  relation_type:       string | null;
  status:              string;
  metadata:            Record<string, string>;
  invite_link_token:   string | null;
  created_at:          string;
}

type Filter = 'all' | 'completed' | 'pending';

const REL_LABEL: Record<string, string> = {
  romantic:     '💕 Romântica',
  friendship:   '🤝 Amizade',
  professional: '💼 Profissional',
  family:       '✨ Familiar',
};

export function MySynastriesPage() {
  const navigate           = useNavigate();
  const [rows,    setRows]    = useState<SynRow[]>([]);
  const [filter,  setFilter]  = useState<Filter>('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase
      .from('synastry_readings')
      .select('id, partner_name, star_score, compatibility_score, relation_type, status, metadata, invite_link_token, created_at')
      .order('created_at', { ascending: false });

    if (filter === 'completed') q = q.eq('status', 'completed');
    if (filter === 'pending')   q = q.eq('status', 'pending');

    const { data } = await q;
    setRows((data ?? []) as SynRow[]);
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id: string) {
    if (!confirm('Excluir esta sinastria permanentemente?')) return;
    await supabase.from('synastry_readings').delete().eq('id', id);
    setRows(prev => prev.filter(r => r.id !== id));
  }

  const pendingCount = rows.filter(r => r.status === 'pending').length;

  return (
    <div className="min-h-screen bg-[#0D1130] py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-[#C9A84C] text-2xl font-light tracking-wider">✦ Suas Sinastrias</h1>
          <p className="text-[#F5ECD7]/50 text-sm mt-1">Histórico de análises de compatibilidade</p>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 mb-8 border-b border-[#C9A84C]/20">
          {([
            { v: 'all',       l: 'Todas' },
            { v: 'completed', l: 'Completas' },
            { v: 'pending',   l: `Aguardando${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          ] as const).map(t => (
            <button
              key={t.v}
              onClick={() => setFilter(t.v)}
              className={`px-4 py-2 text-xs uppercase tracking-wider border-b-2 transition-colors ${
                filter === t.v
                  ? 'border-[#C9A84C] text-[#C9A84C]'
                  : 'border-transparent text-[#F5ECD7]/50 hover:text-[#F5ECD7]'
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {loading && <div className="text-[#F5ECD7]/50 text-sm">Carregando…</div>}

        {!loading && rows.length === 0 && (
          <div className="text-center py-16 border border-[#C9A84C]/20 rounded-sm">
            <div className="text-[#C9A84C] text-3xl mb-4">✦</div>
            <p className="text-[#F5ECD7]/60 mb-6">
              {filter === 'pending' ? 'Nenhum convite pendente' : 'Nenhuma sinastria criada ainda'}
            </p>
            <button
              onClick={() => navigate('/sinastria/criar')}
              className="px-6 py-3 bg-[#C9A84C] text-[#0D1130] text-xs uppercase tracking-[2px] font-bold"
            >
              Criar primeira sinastria
            </button>
          </div>
        )}

        <div className="space-y-4">
          {rows.map(row => {
            const isPending   = row.status === 'pending';
            const meta        = row.metadata ?? {};
            const personA     = meta.person_a_name ?? 'Você';
            const personB     = isPending ? '…' : (row.partner_name ?? meta.person_b_name ?? '');
            const stars       = row.star_score ?? Math.round((row.compatibility_score ?? 60) / 20);
            const starsText   = isPending ? '' : '⭐'.repeat(Math.min(5, Math.max(1, stars)));
            const inviteLink  = row.invite_link_token
              ? `${window.location.origin}/sinastria/convite/${row.invite_link_token}`
              : null;

            return (
              <div key={row.id} className="border border-[#C9A84C]/20 rounded-sm p-5 hover:border-[#C9A84C]/30 transition-colors">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-[#F5ECD7] font-medium">{personA} × {personB}</h3>
                      {isPending && (
                        <span className="border border-yellow-500/40 text-yellow-400 text-xs px-2 py-0.5">
                          Aguardando
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[#F5ECD7]/50 text-xs">
                      {row.relation_type && <span>{REL_LABEL[row.relation_type] ?? row.relation_type}</span>}
                      <span>·</span>
                      <span>{new Date(row.created_at).toLocaleDateString('pt-BR')}</span>
                      {starsText && <><span>·</span><span>{starsText}</span></>}
                    </div>

                    {isPending && inviteLink && (
                      <div className="mt-3 flex gap-2 items-center">
                        <input
                          readOnly
                          value={inviteLink}
                          className="flex-1 bg-transparent border border-[#C9A84C]/20 text-[#F5ECD7]/60 text-xs p-2 rounded-sm min-w-0"
                        />
                        <button
                          onClick={() => navigator.clipboard.writeText(inviteLink)}
                          className="px-3 py-2 border border-[#C9A84C]/30 text-[#C9A84C] text-xs hover:bg-[#C9A84C]/10 flex-shrink-0"
                        >
                          Copiar
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {!isPending && (
                      <button
                        onClick={() => navigate(`/sinastria/${row.id}`)}
                        className="px-4 py-2 border border-[#C9A84C]/30 text-[#C9A84C] text-xs uppercase tracking-wider hover:bg-[#C9A84C]/10 transition-colors"
                      >
                        Ver
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(row.id)}
                      aria-label="Excluir"
                      className="px-3 py-2 text-[#F5ECD7]/30 hover:text-red-400 text-xs transition-colors"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
